/**
 * Customize to Add search event tracking
 */

import { PLPProductCard } from '@/components/blocks/PLPProductCard';
import { MultiFreeGiftPromoContext } from '@/components/blocks/PLPProductCard/context/MultiFreeGiftPromoContext';
import { ProductBadgesPromotions } from '@/components/blocks/PLPProductCard/parts/ProductBadgesPromotions';
import { productGridSx } from '@/components/content/CatalogEntryList/styles/productGrid';
import { useCatalogEntryList } from '@/data/Content/CatalogEntryList';
import {
	getMultiFreeGiftPromoMap,
	useMultiFreeGiftPromoDetails,
} from '@/data/Content/MultiFreeGiftPromoDetails';
import { useHasFreeGiftPromoIconPredicate } from '@/data/utils/usePromoIcons';
import { useWishLists } from '@/data/Content/WishLists';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { ContentContext } from '@/data/context/content';
import { GTMContainerListType } from '@/data/types/GTM';
import { getHref_Product } from '@/utils/getHref_Product';
import { stripBreadcrumbQuery } from '@/utils/stripBreadcrumbQuery';
import { Grid } from '@mui/material';
import { useTrackItemDeliveryPLP } from 'presentation/events/hooks/useTrackItemDeliveryPLP';
import { useTrackSearchResults } from 'presentation/events/hooks/useTrackSearchSubmit';
import { useSyncPromotionListPartnumbers } from 'presentation/events/hooks/useTrackSelectPromotion';
import { useTrackViewItemList } from 'presentation/events/hooks/useTrackViewItemList';
import { FC, useContext, useEffect, useMemo } from 'react';

export const CatalogEntryListProductGrid: FC = () => {
	const {
		products,
		loading,
		clickActionGenerator,
		productListData,
		breadCrumbTrailEntryView: parentCrumb,
		filteredParams: { searchTerm },
		selectedFacet,
		searchSuggestions,
	} = useContext(ContentContext) as ReturnType<typeof useCatalogEntryList> & GTMContainerListType;
	const router = useNextRouter();
	useSyncPromotionListPartnumbers({ products });

	const hasFreeGiftIcon = useHasFreeGiftPromoIconPredicate();
	const pgCatentryIds = useMemo(
		() =>
			products
				.filter((p) => hasFreeGiftIcon(p))
				.map((p) => p.id)
				.join(','),
		[products, hasFreeGiftIcon]
	);
	const { data: batchedPgPromos, loading: pgPromosLoading } =
		useMultiFreeGiftPromoDetails(pgCatentryIds);
	const pgPromoMap = useMemo(() => {
		if (pgPromosLoading) {
			return new Map<string, string>();
		}
		return getMultiFreeGiftPromoMap(batchedPgPromos);
	}, [batchedPgPromos, pgPromosLoading]);
	useTrackViewItemList({
		products,
		listPageName: productListData?.listName,
		listId: productListData?.listId,
		pgPromoMap,
		pgPromoLoading: pgPromosLoading,
	});
	useTrackItemDeliveryPLP({ products });

	const { minPrice, trail } = router.query;
	const { routeUrl, asUrl } = useMemo(() => {
		if (!products.length) return { routeUrl: null, asUrl: null };
		const routeUrl = getHref_Product(products[0], parentCrumb, trail as string[]);
		const asUrl = stripBreadcrumbQuery(routeUrl);
		return { routeUrl, asUrl };
	}, [parentCrumb, products, trail]);
	const willAutoRedirectToPdp =
		!!searchTerm &&
		products.length === 1 &&
		selectedFacet?.length === 0 &&
		minPrice === undefined &&
		!!routeUrl;
	useTrackSearchResults({
		products,
		searchTerm,
		loading,
		pdpRedirectUrl: willAutoRedirectToPdp ? routeUrl?.pathname ?? null : null,
		isNullSearch: !!searchSuggestions?.searchSuggestion,
	});
	const filterKey = Array.isArray(selectedFacet) ? selectedFacet.join('-') : selectedFacet;
	const { wishLists } = useWishLists();
	const wishlistedPartNumbers = useMemo(
		() =>
			new Set(wishLists.flatMap((wishList) => wishList.item?.map((item) => item.partNumber) ?? [])),
		[wishLists]
	);

	useEffect(() => {
		if (willAutoRedirectToPdp && routeUrl) {
			router.replace(routeUrl, asUrl, { shallow: true });
		}
	}, [willAutoRedirectToPdp, routeUrl, asUrl, router]);

	return (
		<MultiFreeGiftPromoContext.Provider value={pgPromoMap}>
			<Grid container>
				<Grid
					container
					item
					spacing="10px"
					alignItems="stretch"
					id="catalog-entry-list-product-grid"
					data-testid="catalog-entry-list-product-grid"
					className="product-listing-container"
				>
					{products?.map((product) => (
						<Grid
							item
							xs={12}
							md={6}
							lg={3}
							display="flex"
							position="relative"
							sx={productGridSx.card}
							id={product.partNumber}
							className="product-container"
							key={`${product.id}-${product.thumbnail}-${filterKey}`}
						>
							<PLPProductCard
								parentCrumb={parentCrumb}
								product={product}
								clickAction={clickActionGenerator(product)}
								isProductInAnyWishlist={wishlistedPartNumbers.has(product.partNumber)}
								showCustomTags
							/>
							<ProductBadgesPromotions product={product} />
						</Grid>
					))}
				</Grid>
			</Grid>
		</MultiFreeGiftPromoContext.Provider>
	);
};
